import React, { useMemo } from 'react';
import { Button } from '@leadsnavi/ui-kit';
import { Maximize2 } from 'lucide-react';
import cn from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { EmailPreviewDialog } from './email-preview-dialog';
import { SubMsgBaseProps, SubMsgCompActions } from '@/features/chat-messages/types/comp-block';

export interface ReplyPreviewCardProps {
  /** 邮件主题 */
  subject: string;
  /** 消息内容 */
  message: string;
  /** 是否聚焦状态 */
  isFocused?: boolean;
  /** 重试回调 */
  onRetry?: () => void;
  /** 插入回调 */
  onInsert?: () => void;
  /** Insert 按钮 loading 状态 */
  insertLoading?: boolean;
  /** 展开/收起回调 */
  onExpand?: () => void;
  /** 自定义类名 */
  className?: string;
}

/**
 * 邮件回复预览卡片组件
 * 展示邮件回复的预览内容，包含主题、消息内容和操作按钮
 */
export const ReplyPreviewCard: React.FC<ReplyPreviewCardProps> = props => {
  const { subject, message, isFocused = false, onRetry, onInsert, insertLoading, onExpand, className } = props;

  return (
    <div
      className={cn(
        'bg-[#F5F5F5] rounded-[12px] flex flex-col gap-[10px] p-[12px_2px_2px]',
        !isFocused ? 'border border-[#E5E5E5]' : '[outline:5px_solid_rgba(107,170,253,0.39)]',
        className
      )}
    >
      {/* 头部 */}
      <div className="flex flex-row items-center justify-between self-stretch gap-2 px-3">
        <span className="text-[#09090B] text-base font-medium flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
          {subject}
        </span>
        <button onClick={onExpand} className="w-4 h-4 flex items-center justify-center cursor-pointer">
          <Maximize2 className="w-4 h-4 text-[#262626]" />
        </button>
      </div>

      {/* 内容气泡 */}
      <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3 self-stretch">
        <p className="text-[#262626] text-sm leading-[150%]  overflow-hidden text-ellipsis whitespace-nowrap">
          {message}
        </p>

        {/* 操作按钮 */}
        <div className="flex flex-row justify-end items-center gap-3 self-stretch">
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
          <Button size="sm" onClick={onInsert} loading={insertLoading}>
            Use
          </Button>
        </div>
      </div>
    </div>
  );
};

export interface EmailPreviewWithCardProps {
  /** 邮件主题 */
  subject: string;
  /** 完整邮件内容 */
  fullContent: string;
  /** 摘要内容（在卡片上显示） */
  summaryContent: string;
  /** 是否聚焦状态 */
  isFocused?: boolean;
  /** 图片 URL（弹窗预览中显示） */
  imageUrl?: string;
  /** 自定义类名 */
  className?: string;
}

/**
 * 带弹窗预览的邮件回复卡片组件
 * 组合 ReplyPreviewCard 和 EmailPreviewDialog，点击展开后弹窗显示完整内容
 */
export const EmailPreviewWithCard: React.FC<EmailPreviewWithCardProps & SubMsgBaseProps> = props => {
  const { subject, summaryContent, isFocused = false, subMsg, msg, sessionId, className, onSomeAction } = props;

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [insertLoading, setInsertLoading] = React.useState(false);

  const handleExpand = useMemoizedFn(() => {
    setDialogOpen(true);
  });

  const handleDialogMinimize = useMemoizedFn(() => {
    setDialogOpen(false);
  });

  const handleCardRetry = useMemoizedFn(async () => {
    await onSomeAction?.('emailCardRetry', {
      originMessageId: msg.messageId,
      originSubMessageId: subMsg.subMessageId,
    } satisfies SubMsgCompActions['emailCardRetry']);
    setDialogOpen(false);
  });

  /** 组合 email-expand 接口入参 */
  const emailExpandParams = useMemo(
    () => ({
      sessionId,
      messageId: msg.messageId,
      subMessageId: subMsg.subMessageId,
    }),
    [sessionId, msg.messageId, subMsg.subMessageId]
  );

  const handleCardInsert = useMemoizedFn(async () => {
    setInsertLoading(true);
    try {
      await onSomeAction?.('replaceEmailContent', {
        ...emailExpandParams,
        ...subMsg.contentValue,
      } as SubMsgCompActions['replaceEmailContent']);
    } finally {
      setInsertLoading(false);
      setDialogOpen(false);
    }
  });

  return (
    <>
      <ReplyPreviewCard
        subject={subject}
        message={summaryContent}
        isFocused={isFocused}
        onRetry={handleCardRetry}
        onInsert={handleCardInsert}
        insertLoading={insertLoading}
        onExpand={handleExpand}
        className={className}
      />

      <EmailPreviewDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onRetry={handleCardRetry}
        onInsert={handleCardInsert}
        onMinimize={handleDialogMinimize}
        emailExpandParams={emailExpandParams}
      />
    </>
  );
};
